define(['moduler'], function (Moduler) {
    'use strict';

    return Moduler.create({
        defaults: {
            wrapper: '<div class="Sticky" />', // set to false to disable wrapper element
            wrapperClass: null,     // additional css class for wrapper
            stickyActiveClass: 'is-sticky', // css class to add when actively sticky
            stickyHiddenClass: 'is-stickyHidden',
            stickyBottomClass: 'is-stickyBottom',
            offsetTop: 0,           // [selector or pixels] offset from where to start being sticky
            widthFromWrapper: true, // the fixed element will have its width from wrapper element
            innerScrolling: true,   // scroll the sticky element if it is taller than the viewport
            contained: false,       // keep element sticky within its container
            autoHide: false,        // will hide sticky element when scrolling down, and show on scroll up
            autoHideOffset: 300,    // extra pixels offset until hidden
            autoShowOffset: 300,    // offset until scrolling up will show element. set to 0 to disable. 
            minScreenWidth: null,   // mininum screen width when sticky is active
            maxScreenWidth: null    // maxium screen width when sticky is active
        },

        init: function () {
            var self = this;

            self.doc = document;
            self.window = window;
            self.wrapper = self.el; // default if wrapper is disabled
            self.isSticky = false; // if element is currently fixed/sticky
            self.isDisabled = false;
            self.cache = {};
            self.offset = 0; // used for inner scrolling offsets
            self.forceShow = false; // force show an auto-hidden sticky element

            if (self.options.wrapper) {
                self.el.classList.add('Sticky-element');

                self.el.insertAdjacentHTML("beforebegin", self.options.wrapper);
                self.el.previousElementSibling.appendChild(self.el);
                self.wrapper = self.el.parentElement;

                if (self.options.wrapperClass) {
                    self.wrapper.classList.add(self.options.wrapperClass);
                }
            }

            self.container = self.wrapper.parentElement;

            if (self.options.contained) {
                // container element has to be relative for "is-stickyBottom" to work
                self.container.style.position = 'relative';
            }

            function recalcAndTick() {
                self.recalc();

                if (!self.isDisabled) {
                    self.tick();
                }
            }

            self.listener = function () {
                (window.requestAnimationFrame && requestAnimationFrame(Moduler.utils.bind(self.tick, self))) || setTimeout(Moduler.utils.bind(self.tick, self), 16);
            };

            window.addEventListener('scroll', self.listener);
            window.addEventListener('resize', self.listener);

            self.listener();

            self.el.addEventListener('recalc', Moduler.utils.bind(recalcAndTick, self));
            self.el.addEventListener('tick', Moduler.utils.bind(self.tick, self));
            self.el.addEventListener('show', Moduler.utils.bind(self.tick, self));
            self.el.addEventListener('enable', Moduler.utils.bind(self.enable, self));
            self.el.addEventListener('disable', Moduler.utils.bind(self.disable, self));
        },

        /*
         * Recalculates the cached properties.
         * This is an expensive method and is only called on resize event 
         * and when height of document changes.
         */
        recalc: function () {
            var self = this,
                cache = self.cache;

            if (typeof (self.options.offsetTop) === 'string') {
                var totalHeight = 0;
                var parsed = parseInt(self.options.offsetTop, 10);
                if (!isNaN(parsed)) {
                    totalHeight = parsed;
                } else {
                    // sum the heights of all elements matching selector
                    document.querySelectorAll(self.options.offsetTop).forEach(o => {
                        totalHeight += self.getHeight(o);
                    });
                }

                cache.offsetTop = totalHeight;
            } else {
                cache.offsetTop = self.options.offsetTop;
            }

            self.el.style.top = cache.offsetTop + "px";

            cache.previousScrollTop = 0;

            // document/window
            cache.documentHeight = self.getHeight(self.doc);
            cache.windowHeight = self.getHeight(self.window);

            // element
            cache.elementHeight = self.getHeight(self.el);

            // wrapper
            cache.wrapperWidth = self.getWidth(self.wrapper);
            cache.wrapperOffsetTop = self.wrapper.offsetTop - cache.offsetTop;

            // container
            cache.containerOffsetTop = self.container.offsetTop;
            cache.containerHeight = self.getHeight(self.container);
            cache.containerEndOffsetTop = self.cache.containerHeight + self.cache.containerOffsetTop - self.cache.elementHeight;

            // auto hide
            cache.autoHideOffsetTop = self.cache.wrapperOffsetTop + self.cache.elementHeight + self.options.autoHideOffset;

            if (!self.isDisabled) {
                // set wrapper to same height as content to take space when content is sticky/fixed.
                self.wrapper.style.height = cache.elementHeight + "px";

                if (self.options.widthFromWrapper) {
                    // update width of sticky element
                    self.el.style.width = cache.wrapperWidth + "px";
                }
            }

            if (self.options.minScreenWidth || self.options.maxScreenWidth) {
                var windowWidth = self.window.outerWidth,
                    isWiderThanMinWidth = !self.options.minScreenWidth || windowWidth > self.options.minScreenWidth,
                    isSmallerThanMaxWidth = !self.options.maxScreenWidth || windowWidth < self.options.maxScreenWidth,
                    isAllowedSpan = isWiderThanMinWidth && isSmallerThanMaxWidth;

                if (isAllowedSpan && self.isDisabled) {
                    self.enable();
                } else if (!isAllowedSpan && !self.isDisabled) {
                    self.disable();
                }
            }
        },

        /*
         * Tick is called on every scroll event.
         */
        tick: function () {
            var self = this,
                newDocHeight = self.getHeight(self.doc);

            if (self.cache.documentHeight != newDocHeight) {
                self.cache.documentHeight = newDocHeight;
                self.recalc();
            }

            var scrollTop = self.window.scrollY,
                shouldStick = self.cache.wrapperOffsetTop < scrollTop,
                deltaScrollTop = scrollTop - self.cache.previousScrollTop,
                isScrollingDown = deltaScrollTop > 0,
                isScrollEnd = scrollTop >= (self.cache.documentHeight - self.cache.windowHeight);

            if (isScrollingDown != self.cache.previousScrollingDown) {
                self.cache.lastDirectionChangePosition = scrollTop;
            }

            if (shouldStick && !self.isSticky) {
                self.el.classList.add(self.options.stickyActiveClass);
                self.isSticky = true;
            } else if (!shouldStick && self.isSticky) {
                self.el.classList.remove(self.options.stickyActiveClass);
                self.isSticky = false;
            }

            if (self.options.autoHide) {
                var shouldAutoHide = self.cache.autoHideOffsetTop < scrollTop && !isScrollEnd;
                var deltaScrollChangePos = Math.abs(scrollTop - self.cache.lastDirectionChangePosition);

                if (isScrollingDown && shouldAutoHide && !self.forceShow) {
                    self.el.classList.add(self.options.stickyHiddenClass);
                } else if (deltaScrollChangePos >= self.options.autoShowOffset || !shouldAutoHide || self.forceShow) {
                    self.el.classList.remove(self.options.stickyHiddenClass);
                    self.forceShow = false;
                }
            }

            if (self.options.innerScrolling) {
                var needsInnerScrolling = self.cache.elementHeight > self.cache.windowHeight;

                if (needsInnerScrolling && self.isSticky) {
                    self.offset -= deltaScrollTop;
                    self.offset = Math.max(self.offset, (self.cache.windowHeight - self.cache.elementHeight));
                    self.offset = Math.min(self.offset, 0);

                    self.el.style.top = self.offset + 'px';
                }
            }

            if (self.options.contained) {
                var hasReachedEnd = (self.cache.containerEndOffsetTop - self.cache.offsetTop - self.offset) < scrollTop;

                if (hasReachedEnd) {
                    self.el.classList.add(self.options.stickyBottomClass);
                } else {
                    self.el.classList.remove(self.options.stickyBottomClass);
                }
            }

            self.cache.previousScrollTop = scrollTop;
            self.cache.previousScrollingDown = isScrollingDown;
        },

        /*
         * To show an auto-hidden sticky element, 
         * trigger the "show" event on the module element.
         */
        show: function () {
            var self = this;

            self.forceShow = true;
            self.tick();
        },

        enable: function () {
            var self = this;

            if (self.isDisabled) {
                self.isDisabled = false;

                window.addEventListener('scroll', self.listener);
                self.recalc();
                self.tick();
            }
        },

        disable: function () {
            var self = this;

            if (!self.isDisabled) {
                self.isDisabled = true;
                self.isSticky = false;
                self.offset = 0;
                self.el.style.top = '';
                self.el.style.width = '';
                self.el.classList.remove(self.options.stickyActiveClass);
                self.el.classList.remove(self.options.stickyBottomClass);
                self.el.classList.remove(self.options.stickyHiddenClass);
                self.wrapper.style.height = '';

                window.removeEventListener('scroll', self.listener);
            }
        },

        getHeight: function (elem) {
            if (elem == document) {
                var body = elem.body,
                    html = elem.documentElement;

                return Math.max(body.scrollHeight, body.offsetHeight,
                    html.clientHeight, html.scrollHeight, html.offsetHeight);
            }

            if (elem == window) {
                return elem.outerHeight;
            }

            return elem.getBoundingClientRect().height;
        },

        getWidth: function (elem) {
            if (elem == document) {
                return elem.documentElement.getBoundingClientRect().width;
            }

            if (elem == window) {
                return elem.outerWidth;
            }

            return elem.getBoundingClientRect().width;
        }
    });
});